import { useState, useRef, useEffect } from "react";
import {
	signInWithEmailAndPassword,
	sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../i18n/i18n";
import AuthForm from "./AuthForm";
import { toast } from "react-toastify";

const LoginForm = () => {
	const { translate } = useTranslation();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState(null);
	const [info, setInfo] = useState(null);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();

	const emailRef = useRef(null);

	// Automatically focus when the modal is ready AND visible
	useEffect(() => {
		// Small delay to ensure the animation is complete
		const timer = setTimeout(() => {
			emailRef.current?.focus();
		}, 400);
		return () => clearTimeout(timer);
	}, []);

	const handleLogin = async e => {
		e.preventDefault();
		setError(null);
		setInfo(null);
		setLoading(true);

		try {
			await signInWithEmailAndPassword(auth, email, password);
			navigate("/home");
		} catch (err) {
			console.error("Erreur de connexion :", err.code, err.message);
			switch (err.code) {
				case "auth/invalid-credential":
					setError(translate("login.error.invalidCredential"));
					break;
				default:
					setError(translate("login.error.generic"));
			}
		} finally {
			setLoading(false);
		}
	};

	// Send password reset email
	const handlePasswordReset = async () => {
		setError(null);
		setInfo(null);

		if (!email) {
			setError(translate("login.error.emailRequired"));
			toast.warning(translate("login.error.emailRequired"));
			return;
		}

		try {
			await sendPasswordResetEmail(auth, email);
			setInfo(translate("alert.resetPasswordSent", { email }));
			toast.success(translate("alert.resetPasswordSent", { email }));
		} catch (err) {
			console.error(translate("login.error.resetPassword"), err?.code || err);
			setError(translate("login.error.resetPassword"));
			toast.error(translate("login.error.resetPassword"));
		}
	};

	return (
		<form
			onSubmit={handleLogin}
			className="card glass-effect auth-card design-form"
		>
			<AuthForm
				email={email}
				setEmail={setEmail}
				password={password}
				setPassword={setPassword}
				error={error}
				translate={translate}
				emailRef={emailRef}
			/>

			{/* Info message */}
			{info && <p className="info-message">{info}</p>}

			<button type="submit" className="primary-button" disabled={loading}>
				{loading ? translate("login.loading") : translate("login.submit")}
			</button>

			{/* Password reset link */}
			<button
				type="button"
				className="text-button"
				onClick={handlePasswordReset}
			>
				{translate("login.forgotPassword")}
			</button>
		</form>
	);
};

export default LoginForm;
